// Container da tabela de templates (dados via hook do domínio).
// Ação de linha (§4.4): abrir editor (contexto livre). "Duplicar" foi
// removido: gerava cópia com duas versões e linha repetida.
import { Building2Icon, InfoIcon } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { DataTable } from "@/components/ui/data-table"
import { Skeleton } from "@/components/ui/skeleton"
import { useAuth } from "@/contexts/auth"
import { useNotificationCenterTemplateSearchParams } from "@/hooks"

import { useNotificationCenterWhiteLabelsTemplates } from "../hooks"
import { filterTemplatesBySearchTerm } from "../template-list-utils"
import { getNotificationCenterTemplateColumns } from "./template-columns"

const templatesPrefix =
  "pages.notificationCenter.templatesPage"

// Skeleton no formato real da tabela de templates.
const TemplatesTableSkeleton = () => (
  <div className="space-y-2 rounded-md border p-4">
    <Skeleton className="h-8 w-full" />
    {Array.from({ length: 6 }).map((_, index) => (
      <Skeleton
        key={`template-skeleton-${index}`}
        className="h-10 w-full"
      />
    ))}
  </div>
)

export const TemplatesTable = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { selectedWhiteLabel } = useAuth()
  const { params } =
    useNotificationCenterTemplateSearchParams()
  const { data, error, isLoading } =
    useNotificationCenterWhiteLabelsTemplates({
      whiteLabelId: selectedWhiteLabel?.uuid || "",
      enabled: params.enabled,
      active: params.active,
      channel: params.channel,
    })

  // Busca por nome OU conteúdo (source) — client-side (ver template-list-utils).
  const filteredData = useMemo(
    () =>
      data
        ? filterTemplatesBySearchTerm(data, params.name)
        : data,
    [data, params.name]
  )

  const columns = useMemo(
    () =>
      getNotificationCenterTemplateColumns(t, {
        onOpenEditor: (template) =>
          navigate(
            `/notification-center/templates/${template.id}`
          ),
      }),
    [t, navigate]
  )

  if (!selectedWhiteLabel) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-l-4 border-amber-400 bg-amber-50 p-6 dark:border-amber-500/60 dark:bg-amber-950/30">
        <InfoIcon
          className="size-6 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden="true"
        />
        <p className="text-base font-semibold text-amber-800 dark:text-amber-200">
          {t(`${templatesPrefix}.selectWhiteLabel`)}
        </p>
      </div>
    )
  }

  if (isLoading) {
    return <TemplatesTableSkeleton />
  }

  if (error) {
    return (
      <p className="rounded-md border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700">
        {t(`${templatesPrefix}.loadError`)}
      </p>
    )
  }

  if (!filteredData?.length) {
    return (
      <p className="rounded-md border p-8 text-center text-sm text-muted-foreground">
        {t(`${templatesPrefix}.emptyState`)}
      </p>
    )
  }

  return (
    <div className="space-y-4">
      {/* Reforça em qual white label os templates estão sendo editados, para
          evitar alterar template do white label errado sem querer. */}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-l-4 border-amber-400 bg-amber-50 px-4 py-2 dark:border-amber-500/60 dark:bg-amber-950/30">
        <Building2Icon
          className="size-4 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden="true"
        />
        <span className="text-sm text-amber-800 dark:text-amber-200">
          {t(`${templatesPrefix}.viewingFor`)}
        </span>
        <span className="text-sm font-bold text-amber-900 dark:text-amber-100">
          {selectedWhiteLabel.name}
        </span>
      </div>
      <DataTable
        data={filteredData}
        columns={columns}
        showColumnVisibility
      />
    </div>
  )
}
